<template>
  <Page>
    <template #header>
      <h1 class="title is-3 has-text-centered">Ajouter un produit</h1>
    </template>
    <template #body>
      <div class="container">
        <div class="columns is-centered">
          <div class="column is-8">

            <!-- Scanner section -->
            <div class="card mb-4">
              <div class="card-header">
                <p class="card-header-title">Scanner un code-barres</p>
              </div>
              <div class="card-content">
                <div v-if="!scannerActive && !scanLoading">
                  <button class="button is-warning is-fullwidth is-medium" @click="startScanner">
                    <span class="icon"><i class="fas fa-barcode"></i></span>
                    <span>Scanner un produit</span>
                  </button>
                </div>

                <div v-if="scannerActive">
                  <div class="video-wrapper mb-3">
                    <video ref="videoEl" class="scanner-video" autoplay muted playsinline></video>
                    <div class="scan-overlay">
                      <div class="scan-line"></div>
                    </div>
                  </div>
                  <p class="has-text-centered has-text-grey is-size-7 mb-2">
                    Pointez la caméra vers le code-barres
                  </p>
                  <button class="button is-light is-fullwidth" @click="stopScanner">
                    <span class="icon"><i class="fas fa-times"></i></span>
                    <span>Annuler</span>
                  </button>
                </div>

                <div v-if="scanLoading" class="has-text-centered py-4">
                  <span class="icon is-large"><i class="fas fa-spinner fa-spin fa-2x"></i></span>
                  <p class="mt-2">Recherche du produit...</p>
                </div>

                <div v-if="scanError" class="notification is-danger is-light mt-3">
                  <button class="delete" @click="scanError = false"></button>
                  <strong>Produit non trouvé</strong> sur Open Food Facts.<br>
                  Remplissez le formulaire manuellement ci-dessous.
                </div>

                <div v-if="scanFoundExisting" class="notification is-warning is-light mt-3">
                  <button class="delete" @click="scanFoundExisting = false"></button>
                  Ce produit est <strong>déjà en base</strong>. Formulaire pré-rempli avec les données existantes.
                </div>
              </div>
            </div>

            <!-- Form -->
            <div class="card">
              <div class="card-header">
                <p class="card-header-title">Informations du produit</p>
              </div>
              <div class="card-content">
                <form @submit.prevent="addProduct">
                  <div class="field">
                    <label class="label">Nom du produit</label>
                    <div class="control has-icons-left">
                      <input
                        type="text"
                        v-model="product.name"
                        placeholder="Ex: Pomme, Poulet, Riz..."
                        class="input"
                        required
                      >
                      <span class="icon is-small is-left">
                        <i class="fas fa-tag"></i>
                      </span>
                    </div>
                  </div>

                  <div class="columns is-multiline">
                    <div class="column is-6">
                      <div class="field">
                        <label class="label">
                          <span class="icon is-small"><i class="fas fa-fire has-text-danger"></i></span>
                          Calories (kcal)
                        </label>
                        <div class="control">
                          <input type="number" v-model="product.kcal" placeholder="0" class="input" step="0.1" min="0" required>
                        </div>
                      </div>
                    </div>
                    <div class="column is-6">
                      <div class="field">
                        <label class="label">
                          <span class="icon is-small"><i class="fas fa-weight has-text-info"></i></span>
                          Protéines (g)
                        </label>
                        <div class="control">
                          <input type="number" v-model="product.proteine" placeholder="0" class="input" step="0.1" min="0" required>
                        </div>
                      </div>
                    </div>
                    <div class="column is-6">
                      <div class="field">
                        <label class="label">
                          <span class="icon is-small"><i class="fas fa-tint has-text-danger"></i></span>
                          Lipides (g)
                        </label>
                        <div class="control">
                          <input type="number" v-model="product.lipide" placeholder="0" class="input" step="0.1" min="0" required>
                        </div>
                      </div>
                    </div>
                    <div class="column is-6">
                      <div class="field">
                        <label class="label">
                          <span class="icon is-small"><i class="fas fa-bread-slice has-text-primary"></i></span>
                          Glucides (g)
                        </label>
                        <div class="control">
                          <input type="number" v-model="product.glucide" placeholder="0" class="input" step="0.1" min="0" required>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div class="notification is-info is-light">
                    <p class="is-size-7"><strong>Note :</strong> Toutes les valeurs sont pour 100 grammes de produit.</p>
                  </div>

                  <div class="field is-grouped is-grouped-right">
                    <div class="control">
                      <button type="button" class="button is-light is-medium" @click="resetForm">
                        <span class="icon"><i class="fas fa-undo"></i></span>
                        <span>Réinitialiser</span>
                      </button>
                    </div>
                    <div class="control">
                      <button type="submit" class="button is-primary is-medium" :class="{ 'is-loading': loading }" :disabled="!isFormValid">
                        <span class="icon"><i class="fas fa-plus"></i></span>
                        <span>Ajouter le produit</span>
                      </button>
                    </div>
                  </div>
                </form>
              </div>
            </div>

          </div>
        </div>
      </div>
    </template>
  </Page>
</template>

<script>
import axios from "axios";
import { base } from "../api";
import Page from "../Page.vue";
import { toastController } from '@ionic/vue';
import { BrowserMultiFormatReader } from '@zxing/browser';

export default {
  name: 'AddProduct',
  components: { Page },
  data() {
    return {
      product: { name: '', kcal: 0, proteine: 0, lipide: 0, glucide: 0 },
      loading: false,
      scannerActive: false,
      scanLoading: false,
      scanError: false,
      scanFoundExisting: false,
      codeReader: null,
      scanControls: null,
    };
  },
  computed: {
    isFormValid() {
      if (!this.product.name || this.product.name.trim() === '') return false;
      const kcal = parseFloat(this.product.kcal);
      const proteine = parseFloat(this.product.proteine);
      const lipide = parseFloat(this.product.lipide);
      const glucide = parseFloat(this.product.glucide);
      return !isNaN(kcal) && kcal >= 0 &&
             !isNaN(proteine) && proteine >= 0 &&
             !isNaN(lipide) && lipide >= 0 &&
             !isNaN(glucide) && glucide >= 0;
    }
  },
  beforeUnmount() {
    this.stopScanner();
  },
  methods: {
    async presentToast(position, message, color) {
      const toast = await toastController.create({ message, duration: 2000, position, color });
      await toast.present();
    },

    resetForm() {
      this.product = { name: '', kcal: 0, proteine: 0, lipide: 0, glucide: 0 };
      this.scanError = false;
      this.scanFoundExisting = false;
    },

    async startScanner() {
      this.scanError = false;
      this.scanFoundExisting = false;
      this.scannerActive = true;
      this.codeReader = new BrowserMultiFormatReader();
      await this.$nextTick();
      try {
        this.scanControls = await this.codeReader.decodeFromVideoDevice(
          undefined,
          this.$refs.videoEl,
          (result, err) => {
            if (result) {
              this.stopScanner();
              this.lookupBarcode(result.getText());
            }
          }
        );
      } catch (e) {
        this.scannerActive = false;
        await this.presentToast('top', 'Impossible d\'accéder à la caméra', 'danger');
      }
    },

    stopScanner() {
      if (this.scanControls) {
        this.scanControls.stop();
        this.scanControls = null;
      }
      this.scannerActive = false;
    },

    async lookupBarcode(barcode) {
      this.scanLoading = true;
      try {
        const { data } = await axios.get(`${base}/product/barcode/${barcode}`);
        this.product = {
          name: data.name,
          kcal: data.kcal,
          proteine: data.proteine,
          lipide: data.lipide,
          glucide: data.glucide,
        };
        this.scanFoundExisting = !!data.already_exists;
      } catch (err) {
        if (err.response?.status === 404) {
          this.scanError = true;
        } else {
          await this.presentToast('top', 'Erreur lors de la recherche du produit', 'danger');
        }
      } finally {
        this.scanLoading = false;
      }
    },

    async addProduct() {
      if (!this.isFormValid) {
        await this.presentToast('top', 'Veuillez remplir tous les champs correctement', 'warning');
        return;
      }
      this.loading = true;
      try {
        await axios.post(base + '/product', {
          name: this.product.name,
          kcal: parseFloat(this.product.kcal),
          proteine: parseFloat(this.product.proteine),
          lipide: parseFloat(this.product.lipide),
          glucide: parseFloat(this.product.glucide),
        });
        await this.presentToast('top', 'Produit ajouté avec succès !', 'success');
        this.resetForm();
      } catch (error) {
        await this.presentToast('top', 'Une erreur est survenue lors de l\'ajout du produit', 'danger');
      } finally {
        this.loading = false;
      }
    },
  }
}
</script>

<style scoped>
.container {
  max-width: 800px;
  margin: 0 auto;
  padding: 0 1rem;
}

.card {
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.card-header {
  background-color: #f8f9fa;
  border-bottom: 1px solid #e9ecef;
}

.card-header-title {
  font-weight: 600;
  color: #495057;
}

.video-wrapper {
  position: relative;
  width: 100%;
  border-radius: 8px;
  overflow: hidden;
  background: #000;
}

.scanner-video {
  width: 100%;
  display: block;
  max-height: 300px;
  object-fit: cover;
}

.scan-overlay {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.scan-line {
  width: 80%;
  height: 2px;
  background: rgba(255, 200, 0, 0.8);
  box-shadow: 0 0 8px rgba(255, 200, 0, 0.8);
  animation: scan 2s ease-in-out infinite;
}

@keyframes scan {
  0%, 100% { transform: translateY(-60px); opacity: 0.6; }
  50% { transform: translateY(60px); opacity: 1; }
}

.field {
  margin-bottom: 1.5rem;
}

.label {
  font-weight: 600;
  color: #495057;
}
</style>
